
import React, { useState } from 'react';
import { FormLabel, FormMessage } from '@/components/ui/form';
import { RadioGroup } from '@/components/ui/radio-group';
import { Loader2 } from 'lucide-react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { SubscriptionPlan } from '@/types/subscriptionPlans';
import BillingCycleSelector from './BillingCycleSelector';
import PlanCard from './PlanCard';
import { classifyFeatures, getPlanPrice } from './utils';

export interface PlanSelectorProps {
  selectedPlanId: string;
  onSelectPlan: (planId: string) => void;
  billingCycle: 'monthly' | 'quarterly' | 'yearly';
  onSelectBillingCycle: (cycle: 'monthly' | 'quarterly' | 'yearly') => void;
  plans?: SubscriptionPlan[];
  isLoading?: boolean;
}

const PlanSelector: React.FC<PlanSelectorProps> = ({
  selectedPlanId,
  onSelectPlan,
  billingCycle,
  onSelectBillingCycle,
  plans = [],
  isLoading = false
}) => {
  const [openCollapsibles, setOpenCollapsibles] = useState<Record<string, boolean>>({});
  
  if (isLoading) {
    return (
      <div className="w-full py-8 flex justify-center items-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading plans...</span>
      </div>
    );
  }
  
  const toggleCollapsible = (planId: string) => {
    setOpenCollapsibles(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };
  
  return (
    <div className="space-y-4">
      <FormLabel className="text-base font-medium">Select a Plan</FormLabel>
      
      <BillingCycleSelector 
        billingCycle={billingCycle}
        onSelectBillingCycle={onSelectBillingCycle}
      />
      
      {/* Wrap the TabsContent in a Tabs component */}
      <Tabs value={billingCycle} className="mt-4">
        <TabsContent value={billingCycle}>
          <RadioGroup 
            value={selectedPlanId} 
            onValueChange={onSelectPlan} 
            className="grid grid-cols-1 md:grid-cols-4 gap-3"
          >
            {plans.map(plan => {
              const { price, billingLabel } = getPlanPrice(plan, billingCycle);
              
              // Classify features as key or other
              const { keyFeatures, otherFeatures } = classifyFeatures(plan.features);
              
              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  selectedPlanId={selectedPlanId}
                  openCollapsibles={openCollapsibles}
                  toggleCollapsible={toggleCollapsible}
                  price={price}
                  billingLabel={billingLabel}
                  keyFeatures={keyFeatures}
                  otherFeatures={otherFeatures}
                />
              );
            })}
          </RadioGroup>
        </TabsContent>
      </Tabs>
      
      <FormMessage />
    </div>
  );
};

export default PlanSelector;
